import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TablePagination,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import PeopleIcon from '@mui/icons-material/People';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { useGetSalesOrdersQuery, useDeleteSalesOrderMutation } from '../../../store/api/salesApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

function SalesOrdersList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Trigger new sale on F2 or Alt+N
      if (e.key === 'F2' || (e.altKey && e.key.toLowerCase() === 'n')) {
        e.preventDefault();
        navigate('/sales/orders/new');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  const toApiDate = (d) =>
    d instanceof Date && !Number.isNaN(d.getTime())
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
          d.getDate(),
        ).padStart(2, '0')}`
      : undefined;

  const { data, isLoading, error } = useGetSalesOrdersQuery({
    page,
    limit: rowsPerPage,
    status: statusFilter || undefined,
    search: search || undefined,
    fromDate: toApiDate(fromDate),
    toDate: toApiDate(toDate),
  });

  const [deleteSalesOrder, { isLoading: isDeleting }] = useDeleteSalesOrderMutation();
  const { data: company } = useGetCurrentCompanyQuery();
  
  const currency = company?.currency || 'USD';
  const dateFormat = company?.dateFormat || 'dd/MM/yyyy';

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)}).format(amount);
    } catch (e) {
      return `${currency} ${amount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };

  const formatDate = (dateValue, timeValue) => {
    if (!dateValue) return '-';
    const dDate = new Date(dateValue);
    if (Number.isNaN(dDate.getTime())) return '-';
    const dTime = timeValue ? new Date(timeValue) : dDate;
    const dd = String(dDate.getDate()).padStart(2, '0');
    const mm = String(dDate.getMonth() + 1).padStart(2, '0');
    const yyyy = dDate.getFullYear();
    let hours = dTime.getHours();
    const minutes = String(dTime.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const timeStr = `${hours}:${minutes} ${ampm}`;
    let dateStr = '';
    switch (dateFormat) {
      case 'MM/dd/yyyy':
        dateStr = `${mm}/${dd}/${yyyy}`;
        break;
      case 'yyyy-MM-dd':
        dateStr = `${yyyy}-${mm}-${dd}`;
        break;
      case 'dd-MM-yyyy':
        dateStr = `${dd}-${mm}-${yyyy}`;
        break;
      case 'dd/MM/yyyy':
      default:
        dateStr = `${dd}/${mm}/${yyyy}`;
    }
    return `${dateStr} ${timeStr}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      confirmed: 'info',
      shipped: 'warning',
      delivered: 'success',
      cancelled: 'error',
      on_hold: 'warning',
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    if (status === 'on_hold') return 'On hold';
    return status ? status.charAt(0).toUpperCase() + status.slice(1) : status;
  };

  const handleOpenDelete = (order) => {
    setSelectedOrder(order);
    setDeleteOpen(true);
  };

  const handleCloseDelete = () => {
    setSelectedOrder(null);
    setDeleteOpen(false);
  };

  const confirmDelete = async () => {
    if (!selectedOrder) return;
    try {
      await deleteSalesOrder(selectedOrder.id).unwrap();
      alert('Sales order deleted and inventory reversed successfully');
      handleCloseDelete();
    } catch (err) {
      alert(err?.data?.message || 'Failed to delete sales order');
    }
  };

  if (isLoading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">Error loading sales orders</Typography>;

  const orders = data?.salesOrders || [];
  const pagination = data?.pagination;

  return (
    <Box>
      {/* ── Header & Action Pills ── */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
            mb: 1.5,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.1rem' }, color: '#0F172A' }}
          >
            Sales
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/sales/orders/new')}
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              px: { xs: 2, sm: 2.5 },
              py: { xs: 1, sm: 1 },
              width: { xs: '100%', sm: 'auto' },
              background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
              boxShadow: '0 4px 12px rgba(14, 165, 233, 0.3)',
              textTransform: 'none',
              fontSize: '0.88rem',
            }}
          >
            New Sales Order (F2)
          </Button>
        </Box>

        {/* Quick Module Navigation Pills Bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            overflowX: 'auto',
            py: 0.5,
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <Chip
            icon={<KeyboardReturnIcon sx={{ fontSize: '16px !important' }} />}
            label="Returns"
            onClick={() => navigate('/sales/returns')}
            clickable
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.78rem', bgcolor: 'background.paper', borderColor: '#CBD5E1', borderRadius: 2 }}
          />
          <Chip
            icon={<ReceiptLongIcon sx={{ fontSize: '16px !important' }} />}
            label="Print List"
            onClick={() => navigate('/sales/print')}
            clickable
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.78rem', bgcolor: 'background.paper', borderColor: '#CBD5E1', borderRadius: 2 }}
          />
          <Chip
            icon={<PeopleIcon sx={{ fontSize: '16px !important' }} />}
            label="Customers"
            onClick={() => navigate('/customers')}
            clickable
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.78rem', bgcolor: 'background.paper', borderColor: '#CBD5E1', borderRadius: 2 }}
          />
          <Chip
            icon={<PauseCircleOutlineIcon sx={{ fontSize: '16px !important' }} />}
            label="Holds"
            onClick={() => navigate('/sales/holds')}
            clickable
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.78rem', bgcolor: 'background.paper', borderColor: '#CBD5E1', borderRadius: 2 }}
          />
        </Box>
      </Box>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Card elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2 }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            {/* Filter Section */}
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, width: '100%' }}>
                <TextField
                  size="small"
                  label="Search (invoice / order #)"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  sx={{ flex: { xs: '1 1 100%', sm: '1 1 220px', md: '0 1 260px' } }}
                />
                <TextField
                  size="small"
                  select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  sx={{ flex: { xs: '1 1 calc(50% - 6px)', sm: '0 1 160px' } }}
                >
                  <MenuItem value="">All statuses</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="on_hold">On hold</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="shipped">Shipped</MenuItem>
                  <MenuItem value="delivered">Delivered</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </TextField>
                <DatePicker
                  label="From date"
                  value={fromDate}
                  onChange={(newValue) => {
                    setFromDate(newValue);
                    setPage(1);
                  }}
                  slotProps={{ textField: { size: 'small', sx: { flex: { xs: '1 1 calc(50% - 6px)', sm: '0 1 140px' } } } }}
                />
                <DatePicker
                  label="To date"
                  value={toDate}
                  onChange={(newValue) => {
                    setToDate(newValue);
                    setPage(1);
                  }}
                  slotProps={{ textField: { size: 'small', sx: { flex: { xs: '1 1 calc(50% - 6px)', sm: '0 1 140px' } } } }}
                />
              </Box>
              {pagination && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {`Page ${pagination.page} of ${pagination.pages || 1} (${pagination.total} total)`}
                </Typography>
              )}
            </Box>

            {/* ── MOBILE VIEW: High-Density Interactive Cards (xs and sm) ── */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5 }}>
              {orders.length > 0 ? (
                orders.map((order) => {
                  const salesTypeDisplay = order.salesType === 'credit'
                    ? 'Credit'
                    : (order.payments && order.payments.length > 0)
                      ? Array.from(new Set(order.payments.map((p) => p.method ? (p.method.charAt(0).toUpperCase() + p.method.slice(1)) : 'Cash'))).join(', ')
                      : (order.salesType || 'Cash');

                  return (
                    <Card
                      key={order.id}
                      elevation={0}
                      sx={{
                        p: 1.75,
                        borderRadius: 2,
                        border: '1px solid #E2E8F0',
                        bgcolor: '#FFFFFF',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                      }}
                    >
                      {/* Top Row: Invoice Number & Status */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography
                          sx={{ fontWeight: 800, color: '#0ea5e9', fontSize: '0.95rem', cursor: 'pointer' }}
                          onClick={() => navigate(`/sales/orders/${order.id}`)}
                        >
                          {order.invoiceNumber || order.orderNumber}
                        </Typography>
                        <Chip
                          label={getStatusLabel(order.status)}
                          color={getStatusColor(order.status)}
                          size="small"
                          sx={{ textTransform: 'capitalize', fontWeight: 700, height: 22, fontSize: '0.68rem' }}
                        />
                      </Box>

                      {/* Middle Row: Customer and Date */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1 }}>
                        <Typography sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.84rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          👤 {order.customer?.name || 'Walk-in Customer'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.72rem', flexShrink: 0 }}>
                          {formatDate(order.invoiceDate || order.orderDate, order.createdAt)}
                        </Typography>
                      </Box>

                      {/* Financial Block: Total & Payment Mode */}
                      <Box
                        sx={{
                          p: 1.25,
                          borderRadius: 1.5,
                          bgcolor: '#F8FAFC',
                          border: '1px solid #E2E8F0',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1.25,
                        }}
                      >
                        <Box>
                          <Typography variant="caption" sx={{ color: '#64748B', display: 'block', fontWeight: 600, fontSize: '0.68rem', textTransform: 'uppercase' }}>
                            Total ({salesTypeDisplay})
                          </Typography>
                          <Typography sx={{ fontWeight: 800, color: '#059669', fontSize: '1.05rem', lineHeight: 1.1 }}>
                            {formatAmount(order.totalAmount)}
                          </Typography>
                        </Box>
                        {Number(order.balanceAmount || 0) > 0 ? (
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" sx={{ color: '#D97706', display: 'block', fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase' }}>
                              Due Balance
                            </Typography>
                            <Typography sx={{ fontWeight: 800, color: '#D97706', fontSize: '0.92rem', lineHeight: 1.1 }}>
                              {formatAmount(order.balanceAmount)}
                            </Typography>
                          </Box>
                        ) : (
                          <Chip
                            label="Paid"
                            size="small"
                            sx={{ height: 20, fontSize: '0.68rem', bgcolor: '#ECFDF5', color: '#059669', fontWeight: 700 }}
                          />
                        )}
                      </Box>

                      {/* Action Buttons Row */}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 0.75, borderTop: '1px solid #F1F5F9' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityIcon sx={{ fontSize: 15 }} />}
                          onClick={() => navigate(`/sales/orders/${order.id}`)}
                          sx={{ height: 30, fontSize: '0.75rem', fontWeight: 600, borderRadius: 1.5, textTransform: 'none' }}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="info"
                          startIcon={<EditIcon sx={{ fontSize: 15 }} />}
                          onClick={() => navigate(`/sales/orders/${order.id}/edit`)}
                          sx={{ height: 30, fontSize: '0.75rem', fontWeight: 600, borderRadius: 1.5, textTransform: 'none' }}
                        >
                          Edit
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDelete(order)}
                          sx={{ p: 0.5 }}
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Card>
                  );
                })
              ) : (
                <Box sx={{ textAlign: 'center', py: 4, bgcolor: '#FFFFFF', borderRadius: 2, border: '1px solid #E2E8F0' }}>
                  <Typography color="text.secondary">No sales orders found</Typography>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={() => navigate('/sales/orders/new')} sx={{ mt: 1.5, borderRadius: 2 }}>
                    Create First Sales Order
                  </Button>
                </Box>
              )}
            </Box>

            {/* ── DESKTOP & TABLET VIEW: Full Data Table (md and up) ── */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Invoice Number</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{`Total Amount (${currency})`}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Sales Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{`Balance Amount (${currency})`}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.length > 0 ? (
                      orders.map((order) => (
                        <TableRow key={order.id} hover>
                          <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {order.invoiceNumber || order.orderNumber}
                          </TableCell>
                          <TableCell>{order.customer?.name || '-'}</TableCell>
                          <TableCell>{formatDate(order.invoiceDate || order.orderDate, order.createdAt)}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: 'success.main' }}>
                            {formatAmount(order.totalAmount)}
                          </TableCell>
                          <TableCell sx={{ textTransform: 'capitalize' }}>
                            {order.salesType === 'credit'
                              ? 'Credit'
                              : (order.payments && order.payments.length > 0)
                                ? Array.from(new Set(order.payments.map((p) => p.method ? (p.method.charAt(0).toUpperCase() + p.method.slice(1)) : 'Cash'))).join(', ')
                                : (order.salesType || 'Cash')}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 500,
                              color:
                                Number(order.balanceAmount || 0) > 0 ? 'warning.main' : 'text.secondary',
                            }}
                          >
                            {Number(order.balanceAmount || 0) > 0
                              ? formatAmount(order.balanceAmount)
                              : '–'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getStatusLabel(order.status)}
                              color={getStatusColor(order.status)}
                              size="small"
                              sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => navigate(`/sales/orders/${order.id}`)}
                                title="View Order"
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => navigate(`/sales/orders/${order.id}/edit`)}
                                title="Edit Order"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleOpenDelete(order)}
                                title="Delete Order"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No sales orders found</Typography>
                          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => navigate('/sales/orders/new')} sx={{ mt: 2 }}>
                            Create First Sales Order
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Pagination Controls */}
            {pagination && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1,
                  mt: 1.5,
                  pt: 1,
                  borderTop: '1px solid #F1F5F9',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {`Page ${pagination.page} of ${pagination.pages || 1} (${pagination.total} total)`}
                </Typography>
                <TablePagination
                  component="div"
                  count={pagination.total}
                  page={(pagination.page || 1) - 1}
                  onPageChange={(_, newPage) => setPage(newPage + 1)}
                  rowsPerPage={pagination.limit || rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    const next = parseInt(e.target.value, 10) || 10;
                    setRowsPerPage(next);
                    setPage(1);
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="Rows per page:"
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </LocalizationProvider>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={handleCloseDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete sales order{' '}
            <strong>{selectedOrder?.orderNumber || selectedOrder?.invoiceNumber}</strong>?
            <br />
            <br />
            This action is permanent. Stock quantities will be reversed, and accounting entries associated with this sale will be removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            autoFocus
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SalesOrdersList;
